import React, { useMemo } from 'react';
import { Box, Typography, styled, useTheme, Card, CardContent, Chip } from '@mui/material';
import { ERadarChart } from '../charts';
import { WaterQualityData } from '../../types/waterQuality';
import { getCropCompatibility } from '../../utils/waterQualityUtils';

interface CropCompatibilityChartProps {
  waterData: WaterQualityData;
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: theme.typography.fontWeightMedium,
}));

const RecommendationCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
}));

const RecommendationChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

export const CropCompatibilityChart: React.FC<CropCompatibilityChartProps> = ({ waterData }) => {
  const theme = useTheme();
  
  // Calculate crop compatibility
  const cropCompatibility = useMemo(() => {
    return getCropCompatibility(waterData);
  }, [waterData]);
  
  // Transform data for radar chart
  const chartData = useMemo(() => {
    const maxScore = Math.max(...cropCompatibility.map(crop => crop.score));
    
    return [
      {
        attribute: 'Compatibility',
        ...cropCompatibility.reduce((acc, crop) => ({
          ...acc,
          [crop.name]: crop.score,
        }), {}),
      }
    ];
  }, [cropCompatibility]);
  
  // Create series for radar chart from crop types
  const series = useMemo(() => {
    return cropCompatibility.map(crop => ({
      dataKey: crop.name,
      name: crop.name,
      color: getColorByScore(crop.score),
    }));
  }, [cropCompatibility]);
  
  // Get color based on compatibility score
  function getColorByScore(score: number): string {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.info.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  }
  
  // Get the best compatible crop
  const bestCrop = useMemo(() => {
    return cropCompatibility.reduce((best, current) => 
      current.score > best.score ? current : best
    , cropCompatibility[0]);
  }, [cropCompatibility]);
  
  // Group recommendations by score
  const recommendations = useMemo(() => {
    const highPriorityRecs = [];
    const medPriorityRecs = [];
    
    // Get recommendations from crops with low compatibility
    for (const crop of cropCompatibility) {
      if (crop.score < 60 && crop.recommendations) {
        if (crop.score < 40) {
          highPriorityRecs.push(...crop.recommendations);
        } else {
          medPriorityRecs.push(...crop.recommendations);
        }
      }
    }
    
    // Remove duplicates
    const uniqueHighPriority = [...new Set(highPriorityRecs)];
    const uniqueMedPriority = [...new Set(medPriorityRecs)];
    
    return {
      high: uniqueHighPriority,
      medium: uniqueMedPriority,
    };
  }, [cropCompatibility]);
  
  return (
    <Box sx={{ mb: 4 }}>
      <SectionTitle variant="h6">Crop Compatibility</SectionTitle>
        <ERadarChart
        data={chartData}
        series={series}
        height={400}
        title="Water Compatibility by Crop Type"
      />
      
      <RecommendationCard>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Best Compatibility: {bestCrop.name} ({bestCrop.score}%)
          </Typography>
          
          {(recommendations.high.length > 0 || recommendations.medium.length > 0) && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Recommendations:
              </Typography>
              
              {recommendations.high.map((rec, index) => (
                <RecommendationChip 
                  key={`high-${index}`} 
                  label={rec} 
                  color="error" 
                  variant="outlined" 
                />
              ))}
              
              {recommendations.medium.map((rec, index) => (
                <RecommendationChip 
                  key={`med-${index}`} 
                  label={rec} 
                  color="warning" 
                  variant="outlined" 
                />
              ))}
            </>
          )}
        </CardContent>
      </RecommendationCard>
    </Box>
  );
};

export default CropCompatibilityChart;
